import React, { FormEvent } from 'react';
import InputBase from '@material-ui/core/InputBase';
import { Keys } from '../../../../utils';
import { CircularProgress } from '../../../ProgressBar/CircularProgress';
import { Components, OptionTypeWithCode } from '../../types';
import { RichTextPresenter } from './RichTextPresenter';

export const AutocompleteInput: NonNullable<Components['Input']> = (props) => {
  const notDisabledCursor = props.autoComplete ? 'text' : 'pointer';
  const cursor = props.params.disabled ? 'default' : notDisabledCursor;
  const autocompleteInProgress = props.open && props.autoComplete;
  const hasSelectedValue = props.selectedOptions && !Array.isArray(props.selectedOptions);
  const showRichText = hasSelectedValue && !autocompleteInProgress;
  const inputValue = showRichText ? '' : props.params.inputProps.value;
  const setInputValue = (e: React.KeyboardEvent | React.MouseEvent, value = '') => {
    if (props.params.inputProps.ref.current) {
      props.params.inputProps.ref.current.value = value;
    }
    props.params.inputProps.onChange({
      ...e,
      target: props.params.inputProps.ref.current
    } as unknown as FormEvent<HTMLInputElement | HTMLTextAreaElement>);
  };
  const selectedLabel = getLabelValue(props.selectedOptions);
  const tempPlaceholder = autocompleteInProgress && hasSelectedValue ? selectedLabel : props.placeholder;
  return (
    <div ref={props.params.InputProps.ref}>
      <InputBase
        {...props.params.InputProps}
        fullWidth={props.params.fullWidth}
        disabled={props.params.disabled}
        required={false}
        onClick={(e) => props?.onClick?.(e)}
        inputProps={{
          ...props.params.inputProps,
          name: props.name,
          value: inputValue,
          'data-is-open': props.open,
          'data-autocomplete-in-progress': autocompleteInProgress,
          'data-test-selected-label': selectedLabel,
          onFocus(e) {
            props.onFocus(true);
            props.params.inputProps.onFocus?.(e);
          },
          onBlur(e) {
            props.onFocus(false);
            props.params.inputProps.onBlur?.(e);
          },
          onMouseDown(e) {
            setInputValue(e);
            props.params.inputProps.onMouseDown(e);
          },
          onKeyDown(e) {
            if (showRichText && !props.open) {
              if (Keys.is(e, 'a', 'ctrl') || Keys.is(e, 'a', 'meta')) {
                setInputValue(e, getLabelValue(props.selectedOptions));
                props.params.inputProps.onMouseDown(e as unknown as React.MouseEvent<HTMLInputElement>);
              }
              if (Keys.is(e, 'backspace')) {
                setInputValue(e);
                props.params.inputProps.onMouseDown(e as unknown as React.MouseEvent<HTMLInputElement>);
              }
              if (Keys.isOneOf(e, ['arrowLeft', 'arrowRight'])) {
                setInputValue(e, getLabelValue(props.selectedOptions));
              }
            }
          },
          style: {
            cursor,
            width: !props.autoComplete ? 1 : undefined,
            minWidth: !props.autoComplete ? 1 : undefined
          }
        }}
        style={{ cursor }}
        endAdornment={
          <>
            {props.loading ? <CircularProgress color="inherit" size={20} className="loading" /> : undefined}
            {props.params.InputProps.endAdornment}
          </>
        }
        startAdornment={
          showRichText ? (
            <RichTextPresenter
              selectedOption={props.selectedOptions as OptionTypeWithCode}
              visibleElementsWhenSelected={props.visibleElementsWhenSelected}
              dataTest="selected-value"
              onClick={(e) => {
                setInputValue(e);
                props.params.inputProps.onMouseDown?.(e);
                props?.onClick?.(e);
              }}
            />
          ) : (
            props.params.InputProps.startAdornment
          )
        }
        placeholder={showRichText ? '' : tempPlaceholder}
        readOnly={!props.autoComplete}
      />
    </div>
  );
};

function getLabelValue(selectedOption: OptionTypeWithCode | OptionTypeWithCode[] | null): string {
  if (!selectedOption || Array.isArray(selectedOption)) {
    return '';
  }
  return selectedOption.label;
}
